import { useEffect } from 'react';
import { create } from 'zustand';
import { FEATURE_FLAGS } from '~/constants';
import { PlatformData } from '~/hooks/api/useOPCreatorSearch';
import { SmartAccountsType } from '~/hooks/api/useSearchSmartProfiles';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

import { TalentValues } from './ProfileInformation/ProfileInformation';

type AddTalentStore = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: AddTalentStore['isModalOpen']) => void;

  // Form state persistence
  formValues: Partial<TalentValues> | null;
  setFormValues: (values: Partial<TalentValues> | null) => void;
  isFormTouched: boolean;
  setIsFormTouched: (touched: boolean) => void;

  // Steps
  step: number;
  /* goToNextStep: increments the step value */
  goToNextStep: () => void;
  /* goToPreviousStep: decrements the step value */
  goToPreviousStep: () => void;
  /* goToStep: sets the specfic step value */
  goToStep: (step: number) => void;

  // metadata
  searchResult?: undefined | PlatformData;
  setSearchResult: (data: AddTalentStore['searchResult']) => void;
  searchedHandle?: string;
  setSearchedHandle: (data: AddTalentStore['searchedHandle']) => void;
  searchedPlatform?: string;
  setSearchedPlatform: (data: AddTalentStore['searchedPlatform']) => void;

  platforms: (SmartAccountsType | PlatformData)[];
  setPlatforms: (data: AddTalentStore['platforms']) => void;

  /* Smart Profiles */
  smartProfileEnabled: boolean;
  setSmartProfileEnabled: (status: boolean) => void;

  smartProfileRequestId: string | undefined;
  setSmartProfileRequestId: (requestId: string | undefined) => void;
  /* Smart Profiles */

  // Method to reset smart profile state
  resetSmartProfileState: () => void;
  resetProfileFormState: () => void;
  reset: () => void;
};

// whalar-mode flow also make use of this store
// please be aware in case any changes are done
export const addTalentStore = create<AddTalentStore>((set, get) => ({
  isModalOpen: false,
  setIsModalOpen: (isOpen) => set({ isModalOpen: isOpen }),

  // Form state persistence
  formValues: null,
  setFormValues: (values) => set({ formValues: values }),
  isFormTouched: false,
  setIsFormTouched: (touched) => set({ isFormTouched: touched }),

  step: 0,
  goToNextStep: () => set({ step: get().step + 1 }),
  goToPreviousStep: () => set({ step: get().step - 1 }),
  goToStep: (step) => set({ step }),

  // Metadata
  searchedPlatform: undefined,
  searchedHandle: undefined,
  searchResult: undefined,
  setSearchedPlatform: (platform) => set({ searchedPlatform: platform }),
  setSearchedHandle: (handle) => set({ searchedHandle: handle }),
  setSearchResult: (data) => set({ searchResult: data }),

  platforms: [],
  setPlatforms: (platforms) => set({ platforms }),

  /* Smart Profiles */
  smartProfileEnabled: true,
  setSmartProfileEnabled: (status) => set({ smartProfileEnabled: status }),

  smartProfileRequestId: undefined,
  setSmartProfileRequestId: (requestId) =>
    set({ smartProfileRequestId: requestId }),

  resetProfileFormState: () =>
    set({
      formValues: null,
      isFormTouched: false,
    }),
  /* Smart Profiles */
  resetSmartProfileState: () =>
    set({
      smartProfileEnabled: true,
      smartProfileRequestId: undefined,
    }),
  // Reset
  reset: () =>
    set({
      isModalOpen: false,
      step: 0,
      searchedPlatform: undefined,
      platforms: [],
      searchedHandle: undefined,
      searchResult: undefined,
      smartProfileEnabled: true,
      smartProfileRequestId: undefined,
      formValues: null,
      isFormTouched: false,
    }),
}));

export const useAddTalentStore = () => {
  const store = addTalentStore();

  const foamSmartCreatorProfilesEnabled = useFeatureFlag(
    FEATURE_FLAGS.foamSmartCreatorProfiles,
  );

  // no FFs available for whalar mode users
  // so we need avoid this automatic set setSmartProfileEnabled(false) for wm users
  useEffect(() => {
    if (foamSmartCreatorProfilesEnabled === 'disabled') {
      store.setSmartProfileEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foamSmartCreatorProfilesEnabled]);

  return store;
};
